import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  type MutableRefObject,
} from 'react';
import { LiveSiteClicksOnMenuDropdownAndOptionInsideButtonName } from '@wix/restaurants-bi';

import { useOnlineOrdersStorage } from '../../storage';
import { useProcessedMenus } from '../../hooks/useProcessedMenus';
import {
  buildSectionTabs,
  buildMenuOptions,
} from '../../../../utils/menuNavigation';
import {
  MENU_QUERY_PARAMS,
  FREE_TIER_BANNER_HEIGHT_PX,
} from '../../../../utils/constants';

import { NAVIGATION_ROOT_ID } from './constants';

const SCROLL_LOCK_DURATION_MS = 1000;
const OBSERVER_TOP_MARGIN = '-10%';
const OBSERVER_BOTTOM_MARGIN = '-80%';
const SECTION_SELECTOR = '[data-section-id]';

export const useHooks = () => {
  const {
    cartQuantities,
    openCart,
    isPremium,
    reportMenuBarClick,
    reportMenuDropdownClick,
  } = useOnlineOrdersStorage();
  const { processedMenus: menus } = useProcessedMenus();

  const hasMultipleMenus = menus.length > 1;

  const menuOptions = useMemo(() => buildMenuOptions(menus), [menus]);

  const [selectedMenuId, setSelectedMenuId] = useState('');

  const activeMenuId = selectedMenuId || menus[0]?.id;

  const sectionTabs = useMemo(
    () => buildSectionTabs(menus, activeMenuId),
    [menus, activeMenuId],
  );

  const [activeSectionId, setActiveSectionId] = useState('');

  const activeTabId = activeSectionId || sectionTabs[0]?.id;

  const isScrollingToSectionRef = useRef(false);
  const scrollLockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useScrollToMenuOrMenuSection({
    menus,
    sectionTabs,
    isPremium,
    isScrollingToSectionRef,
    setSelectedMenuId,
    setActiveSectionId,
  });

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>(SECTION_SELECTOR);

    if (elements.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingToSectionRef.current) {
          return;
        }

        const intersecting = entries.filter((e) => e.isIntersecting);

        if (intersecting.length === 0) {
          return;
        }

        const canonical = intersecting.reduce((best, entry) =>
          entry.boundingClientRect.top < best.boundingClientRect.top
            ? entry
            : best,
        );

        const sectionId = (canonical.target as HTMLElement).dataset.sectionId;

        if (sectionId) {
          setActiveSectionId(sectionId);

          const parentMenu = menus.find((m) =>
            m.sections?.some((s) => s.id === sectionId),
          );

          if (parentMenu) {
            setSelectedMenuId(parentMenu.id);
          }
        }
      },
      {
        rootMargin: `${OBSERVER_TOP_MARGIN} 0px ${OBSERVER_BOTTOM_MARGIN} 0px`,
        threshold: 0,
      },
    );

    for (const el of elements) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sectionTabs, menus]);

  const onSectionTabClick = useCallback(
    (sectionId: string) => {
      const sectionIndex = sectionTabs.findIndex((tab) => tab.id === sectionId);

      reportMenuBarClick({
        sectionId,
        sectionName: sectionTabs[sectionIndex]?.label,
        currentSectionNumber: sectionIndex >= 0 ? sectionIndex + 1 : undefined,
        numTotalSections: sectionTabs.length,
      });

      setActiveSectionId(sectionId);
      isScrollingToSectionRef.current = true;

      const sectionElement = document.querySelector(
        `[data-section-id="${sectionId}"]`,
      );

      scrollToElementWithNavbarOffset(sectionElement, isPremium);

      clearTimeout(scrollLockTimeoutRef.current ?? undefined);
      scrollLockTimeoutRef.current = setTimeout(() => {
        isScrollingToSectionRef.current = false;
      }, SCROLL_LOCK_DURATION_MS);
    },
    [sectionTabs, reportMenuBarClick, isPremium],
  );

  const onMenuChange = useCallback(
    (menuId: string) => {
      const currentMenuIndex = menuOptions.findIndex(
        (option) => option.id === activeMenuId,
      );
      const selectedMenuIndex = menuOptions.findIndex(
        (option) => option.id === menuId,
      );

      reportMenuDropdownClick({
        buttonName:
          LiveSiteClicksOnMenuDropdownAndOptionInsideButtonName.DROPDOWN_MENU_OPTION,
        currentMenuId: activeMenuId,
        currentMenuName: menuOptions[currentMenuIndex]?.value,
        currentMenuNumber:
          currentMenuIndex >= 0 ? currentMenuIndex + 1 : undefined,
        menuId,
        menuName: menuOptions[selectedMenuIndex]?.value,
        menuNumber: selectedMenuIndex >= 0 ? selectedMenuIndex + 1 : undefined,
        numTotalMenus: menuOptions.length,
      });

      setSelectedMenuId(menuId);
      setActiveSectionId('');

      isScrollingToSectionRef.current = true;

      const menuElement = document.querySelector(`[data-menu-id="${menuId}"]`);

      scrollToElementWithNavbarOffset(menuElement, isPremium);

      clearTimeout(scrollLockTimeoutRef.current ?? undefined);
      scrollLockTimeoutRef.current = setTimeout(() => {
        isScrollingToSectionRef.current = false;
      }, SCROLL_LOCK_DURATION_MS);
    },
    [menuOptions, activeMenuId, reportMenuDropdownClick, isPremium],
  );

  const cartCount = Object.values(cartQuantities).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  return {
    hasMultipleMenus,
    menuOptions,
    activeMenuId,
    onMenuChange,
    sectionTabs,
    activeTabId,
    onSectionTabClick,
    cartCount,
    openCart: () => openCart(),
    isPremium,
  };
};

type ScrollToMenuOrSectionParams = {
  menus: ReturnType<typeof useOnlineOrdersStorage>['menus'];
  sectionTabs: { id: string; label: string }[];
  isPremium: boolean;
  isScrollingToSectionRef: MutableRefObject<boolean>;
  setSelectedMenuId: (id: string) => void;
  setActiveSectionId: (id: string) => void;
};

/**
 * On initial page load, scrolls to the menu/section specified in the URL params.
 * `?menuId=<id>` → scrolls to the menu.
 * `?sectionId=<id>&menuId=<id>` → scrolls to the section.
 */
const useScrollToMenuOrMenuSection = ({
  menus,
  sectionTabs,
  isPremium,
  isScrollingToSectionRef,
  setSelectedMenuId,
  setActiveSectionId,
}: ScrollToMenuOrSectionParams) => {
  const hasInitialScrolledRef = useRef(false);

  useEffect(() => {
    if (hasInitialScrolledRef.current || sectionTabs.length === 0) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const itemId = params.get(MENU_QUERY_PARAMS.itemId);
    const sectionId = params.get(MENU_QUERY_PARAMS.sectionId);
    const menuId = params.get(MENU_QUERY_PARAMS.menuId);

    if (itemId) {
      hasInitialScrolledRef.current = true;

      return;
    }

    hasInitialScrolledRef.current = true;

    if (sectionId) {
      const parentMenu = menus.find((m) =>
        m.sections.some((s) => s.id === sectionId),
      );

      if (parentMenu) {
        setSelectedMenuId(parentMenu.id);
      }

      setActiveSectionId(sectionId);
      scrollToElementWithLock(
        document.querySelector(`[data-section-id="${sectionId}"]`),
      );
    } else if (menuId) {
      setSelectedMenuId(menuId);
      scrollToElementWithLock(
        document.querySelector(`[data-menu-id="${menuId}"]`),
      );
    }

    function scrollToElementWithLock(element: Element | null) {
      isScrollingToSectionRef.current = true;
      scrollToElementWithNavbarOffset(element, isPremium);
      setTimeout(() => {
        isScrollingToSectionRef.current = false;
      }, SCROLL_LOCK_DURATION_MS);
    }
  }, [
    sectionTabs,
    menus,
    isScrollingToSectionRef,
    setSelectedMenuId,
    setActiveSectionId,
  ]);
};

function scrollToElementWithNavbarOffset(
  element: Element | null,
  isPremium: boolean,
) {
  if (!element) {
    return;
  }

  const navbar = document.getElementById(NAVIGATION_ROOT_ID);

  if (!navbar) {
    return;
  }

  const navBarHeight = navbar.getBoundingClientRect().height ?? 0;
  const navBarStickyTopOffset = isPremium ? 0 : FREE_TIER_BANNER_HEIGHT_PX;

  const top =
    element.getBoundingClientRect().top +
    window.scrollY -
    navBarHeight -
    navBarStickyTopOffset;

  window.scrollTo({ top, behavior: 'smooth' });
}
